import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppButton } from "./primitives";
import { onConfirmRequest, onMessageRequest, type AppConfirmOptions, type AppMessageOptions } from "./confirmDialog";

type PendingConfirm = AppConfirmOptions & {
  resolve: (value: boolean) => void;
};

type Toast = AppMessageOptions & {
  id: number;
};

export const ConfirmDialogHost = () => {
  const { t } = useTranslation("common");
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(
    () =>
      onConfirmRequest((detail) => {
        setPending(detail);
      }),
    [],
  );

  useEffect(
    () =>
      onMessageRequest((detail) => {
        const id = Date.now();
        setToast({ ...detail, id });
        window.setTimeout(() => {
          setToast((current) => (current?.id === id ? null : current));
        }, 4200);
      }),
    [],
  );

  const close = useCallback((value: boolean) => {
    setPending((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      const previousFocus = previousFocusRef.current;
      if (previousFocus?.isConnected) previousFocus.focus();
      previousFocusRef.current = null;
    };
  }, [close, pending]);

  const toastTone = toast?.tone === "danger"
    ? "border-[color:color-mix(in_srgb,var(--app-danger)_24%,var(--app-border)_76%)] bg-[var(--app-danger-soft)] text-[var(--app-danger)]"
    : toast?.tone === "success"
      ? "border-[color:color-mix(in_srgb,var(--app-success)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-success)_10%,var(--app-surface)_90%)] text-[var(--app-success)]"
      : "border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text)]";

  return (
    <>
      {pending ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[var(--app-overlay)] p-3 backdrop-blur-sm sm:p-6"
          role="presentation"
          onMouseDown={() => close(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              close(false);
              return;
            }
            if (event.key !== "Tab") return;
            const dialog = dialogRef.current;
            if (!dialog) return;
            const focusable = Array.from(
              dialog.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
              ),
            ).filter((element) => element.getClientRects().length > 0);
            if (focusable.length === 0) {
              event.preventDefault();
              dialog.focus();
              return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (document.activeElement === dialog) {
              event.preventDefault();
              (event.shiftKey ? last : first)?.focus();
            } else if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }}
        >
          <section
            ref={dialogRef}
            className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto rounded-[var(--ds-radius-lg)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-5 text-[var(--ds-ink)] shadow-[var(--ds-shadow-4)] sm:max-h-[calc(100dvh-3rem)] sm:p-6"
            role={pending.tone === "danger" ? "alertdialog" : "dialog"}
            aria-modal="true"
            aria-labelledby="app-confirm-title"
            aria-describedby={[
              pending.body ? "app-confirm-body" : undefined,
              pending.details?.length ? "app-confirm-details" : undefined,
            ].filter(Boolean).join(" ") || undefined}
            tabIndex={-1}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--app-muted-text)]">
              {pending.tone === "danger" ? t("common.dialogs.confirmDeletion") : t("common.dialogs.confirmAction")}
            </p>
            <h2 id="app-confirm-title" className="text-2xl font-semibold leading-tight tracking-[-0.04em]">
              {pending.title}
            </h2>
            {pending.body ? (
              <p id="app-confirm-body" className="mt-3 text-sm leading-6 text-[var(--app-text-secondary)]">
                {pending.body}
              </p>
            ) : null}
            {pending.details?.length ? (
              <ul
                id="app-confirm-details"
                className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6 text-[var(--app-text-secondary)]"
              >
                {pending.details.map((detail) => <li key={detail}>{detail}</li>)}
              </ul>
            ) : null}
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <AppButton ref={cancelRef} type="button" tone="secondary" onClick={() => close(false)}>
                {pending.cancelLabel ?? t("common.actions.cancel")}
              </AppButton>
              <AppButton type="button" tone={pending.tone === "danger" ? "danger" : "primary"} onClick={() => close(true)}>
                {pending.confirmLabel ?? t("common.actions.confirm")}
              </AppButton>
            </div>
          </section>
        </div>
      ) : null}

      {toast ? (
        <div className={`fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-[95] mx-auto max-w-md rounded-[var(--ds-radius-lg)] border px-4 py-3 shadow-[var(--ds-shadow-3)] backdrop-blur md:bottom-6 ${toastTone}`} role="status" aria-live="polite">
          <p className="text-sm font-semibold">{toast.title}</p>
          {toast.body ? <p className="mt-1 text-sm leading-5 opacity-85">{toast.body}</p> : null}
        </div>
      ) : null}
    </>
  );
};
